/**
 * Offer a catalogue add-on to a tenant (TMMPDM-0019). Only ACTIVE catalogue items are offered.
 * Choosing one prefills the price (its default price, editable), currency, period and allowed
 * billing from the item. The tenant admin accepts elsewhere; offering charges nothing. A 409
 * (`ADDON_ACTIVE`, `ADDON_OFFER_EXISTS`, …) shows the server's message here; a
 * `VALIDATION_FAILED` lands on its fields.
 */
import { useState } from 'react';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  MenuItem,
  Stack,
  TextField,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { useCopy } from '@/hooks/useCopy';
import { ErrorState, SubmitButton } from '@/components/page';
import { adminApi } from '@/api/tmm/admin';
import { ApiError, errorCopy, isValidationFailed, validationFields } from '@/api/errors';
import type {
  AddonAllowedBilling,
  AddonPeriod,
  CatalogView,
  OfferRequest,
  TenantAddonView,
} from '@/api/tmm/types';
import { AppSwitch } from '@/components/ui/AppSwitch';
import { parseMajor } from '@/routes/rateCards/rateCardInputs';
import { useCatalog } from '@/routes/addons/useCatalog';
import { majorFromMinor } from '@/format';
import {
  ALLOWED_BILLING_FALLBACK,
  currencyOptions,
  PERIOD_FALLBACK,
  invalidMoney,
} from '@/routes/addons/addonFormat';

const PERIODS: AddonPeriod[] = ['ANNUAL', 'MONTHLY'];
const ALLOWED_BILLING: AddonAllowedBilling[] = ['BOTH', 'CARD', 'NET_FROM_PAYOUTS'];
const NOTES_MAX = 2000;

interface OfferAddonDialogProps {
  open: boolean;
  slug: string;
  onClose: () => void;
  onOffered: (addon: TenantAddonView) => void;
}

export function OfferAddonDialog({ open, slug, onClose, onOffered }: OfferAddonDialogProps) {
  const { t } = useCopy();
  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{t('addons.offer.title', 'Offer add-on')}</DialogTitle>
      {open ? <OfferAddonForm slug={slug} onClose={onClose} onOffered={onOffered} /> : null}
    </Dialog>
  );
}

function OfferAddonForm({ slug, onClose, onOffered }: Omit<OfferAddonDialogProps, 'open'>) {
  const { t } = useCopy();
  const catalog = useCatalog();
  const offerable = catalog.items.filter((item) => item.status === 'ACTIVE');
  const [kind, setKind] = useState('');
  const [price, setPrice] = useState('');
  const [currency, setCurrency] = useState('USD');
  const [period, setPeriod] = useState<AddonPeriod>('ANNUAL');
  const [allowedBilling, setAllowedBilling] = useState<AddonAllowedBilling>('BOTH');
  const [autoRenew, setAutoRenew] = useState(true);
  const [termStart, setTermStart] = useState('');
  const [notes, setNotes] = useState('');
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [merchantUnset, setMerchantUnset] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const currencies = currencyOptions(currency);

  const choose = (item: CatalogView) => {
    setKind(item.kind);
    setPrice(
      item.defaultPriceMinor === undefined
        ? ''
        : majorFromMinor(item.defaultPriceMinor, item.currency),
    );
    setCurrency(item.currency);
    setPeriod(item.period);
    setAllowedBilling(item.allowedBilling);
  };

  const validate = (): Record<string, string> => {
    const next: Record<string, string> = {};
    if (kind === '') next.addonKind = t('addons.offer.chooseAddon', 'Choose an add-on.');
    if (price.trim() === '') next.price = t('addons.form.required', 'Required.');
    else if (invalidMoney(price))
      next.price = t('addons.form.nonNegative', 'Must be zero or greater.');
    if (notes.trim().length > NOTES_MAX) {
      next.notes = t('addons.form.tooLong', 'At most {{max}} characters.', { max: NOTES_MAX });
    }
    return next;
  };

  const submit = async () => {
    const next = validate();
    setFieldErrors(next);
    setFormError(null);
    setMerchantUnset(false);
    if (Object.keys(next).length > 0) return;
    const body: OfferRequest = {
      addonKind: kind,
      priceMinor: parseMajor(price.trim(), currency),
      currency,
      autoRenew,
      period,
      allowedBilling,
    };
    if (termStart !== '') body.termStart = termStart;
    if (notes.trim() !== '') body.notes = notes.trim();
    setSubmitting(true);
    try {
      onOffered(await adminApi.offerAddon(slug, body));
    } catch (err: unknown) {
      if (isValidationFailed(err)) {
        const fields = validationFields(err, t);
        const known = [
          'addonKind',
          'priceMinor',
          'currency',
          'period',
          'allowedBilling',
          'termStart',
          'notes',
        ] as const;
        setFieldErrors({
          addonKind: fields.addonKind ?? '',
          price: fields.priceMinor ?? '',
          currency: fields.currency ?? '',
          period: fields.period ?? '',
          allowedBilling: fields.allowedBilling ?? '',
          termStart: fields.termStart ?? '',
          notes: fields.notes ?? '',
        });
        // A field the form doesn't show is said once, in the banner; shown fields are said at the field.
        if (Object.keys(fields).some((key) => !(known as readonly string[]).includes(key))) {
          setFormError(errorCopy(err, t, t('addons.actionError', 'Could not update the add-on.')));
        }
      } else if (err instanceof ApiError && String(err.code) === 'MERCHANT_OF_RECORD_UNSET') {
        setMerchantUnset(true);
        setFormError(t('addons.merchantUnset', 'Merchant of record is not set.'));
      } else {
        setFormError(errorCopy(err, t, t('addons.actionError', 'Could not update the add-on.')));
      }
    } finally {
      setSubmitting(false);
    }
  };

  const priceMinor =
    price.trim() === '' || invalidMoney(price) ? null : parseMajor(price.trim(), currency);

  return (
    <>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {formError ? (
            <Alert severity="error">
              {formError}
              {merchantUnset ? (
                <>
                  {' '}
                  <Button
                    component={RouterLink}
                    to="/product-settings?tab=merchant"
                    size="small"
                    color="inherit"
                  >
                    {t('addons.openSettings', 'Open Settings')}
                  </Button>
                </>
              ) : null}
            </Alert>
          ) : null}
          {catalog.error ? (
            <ErrorState
              error={catalog.error}
              message={t('addons.catalog.loadError', 'Could not load the catalogue.')}
            />
          ) : null}
          <Grid container spacing={2}>
            <Grid size={12}>
              <TextField
                select
                label={t('addons.offer.addon', 'Add-on')}
                value={kind}
                onChange={(event) => {
                  const item = offerable.find((row) => row.kind === event.target.value);
                  if (item) choose(item);
                }}
                error={Boolean(fieldErrors.addonKind)}
                helperText={
                  fieldErrors.addonKind ||
                  (!catalog.loading && offerable.length === 0
                    ? t('addons.offer.noneActive', 'No active add-ons in the catalogue.')
                    : undefined)
                }
                required
                fullWidth
              >
                {offerable.map((item) => (
                  <MenuItem key={item.kind} value={item.kind}>
                    {item.name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                label={t('addons.offer.price', 'Price per period')}
                value={price}
                onChange={(event) => setPrice(event.target.value)}
                error={Boolean(fieldErrors.price)}
                helperText={
                  fieldErrors.price ||
                  (priceMinor === null
                    ? undefined
                    : t('addons.form.minorCaption', 'Sends {{minor}} minor units', {
                        minor: priceMinor,
                      }))
                }
                required
                fullWidth
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                select
                label={t('addons.form.currency', 'Currency')}
                value={currency}
                onChange={(event) => setCurrency(event.target.value)}
                error={Boolean(fieldErrors.currency)}
                helperText={fieldErrors.currency}
                fullWidth
              >
                {currencies.map((code) => (
                  <MenuItem key={code} value={code}>
                    {code}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                select
                label={t('addons.form.period', 'Period')}
                value={period}
                onChange={(event) => setPeriod(event.target.value as AddonPeriod)}
                error={Boolean(fieldErrors.period)}
                helperText={fieldErrors.period}
                fullWidth
              >
                {PERIODS.map((value) => (
                  <MenuItem key={value} value={value}>
                    {t(`addons.period.${value}`, PERIOD_FALLBACK[value])}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                select
                label={t('addons.form.allowedBilling', 'Allowed billing')}
                value={allowedBilling}
                onChange={(event) => setAllowedBilling(event.target.value as AddonAllowedBilling)}
                error={Boolean(fieldErrors.allowedBilling)}
                helperText={fieldErrors.allowedBilling}
                fullWidth
              >
                {ALLOWED_BILLING.map((value) => (
                  <MenuItem key={value} value={value}>
                    {t(`addons.allowedBilling.${value}`, ALLOWED_BILLING_FALLBACK[value])}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                label={t('addons.offer.termStart', 'Term start (optional)')}
                type="date"
                value={termStart}
                onChange={(event) => setTermStart(event.target.value)}
                error={Boolean(fieldErrors.termStart)}
                helperText={fieldErrors.termStart}
                slotProps={{ inputLabel: { shrink: true } }}
                fullWidth
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <AppSwitch
                label={t('addons.offer.autoRenew', 'Auto-renew')}
                checked={autoRenew}
                onChange={setAutoRenew}
              />
            </Grid>
            <Grid size={12}>
              <TextField
                label={t('addons.offer.notes', 'Notes (optional)')}
                value={notes}
                onChange={(event) => setNotes(event.target.value)}
                error={Boolean(fieldErrors.notes)}
                helperText={fieldErrors.notes}
                multiline
                minRows={2}
                fullWidth
                slotProps={{ htmlInput: { maxLength: NOTES_MAX } }}
              />
            </Grid>
          </Grid>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('addons.actions.cancel', 'Cancel')}</Button>
        <SubmitButton
          onClick={() => void submit()}
          busy={submitting}
          disabled={offerable.length === 0}
        >
          {t('addons.offer.submit', 'Offer')}
        </SubmitButton>
      </DialogActions>
    </>
  );
}
